import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  message,
  Tag,
  Space,
  Card,
  Statistic,
  DatePicker,
  Select,
  Form,
  ConfigProvider,
  Modal,
  Spin,
  Alert,
  Radio,
} from "antd";
import {
  LoadingOutlined,
  InfoCircleOutlined,
  StopOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import deDE from "antd/locale/de_DE";
import dayjs from "dayjs";
import "dayjs/locale/de";
import axios from "axios";
import { API_BASE_URL } from "../api/axios";
import { Link } from "react-router-dom";
import { getPlanningModeLabels } from "../utils/planningMode";

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
};

const ROUTE = API_BASE_URL.replace(/\/$/, "");

const BUNDESLAENDER = [
  { label: "Ohne Berücksichtigung der Feiertage", value: "NONE" },
  { label: "Baden-Württemberg", value: "BW" },
  { label: "Bayern", value: "BY" },
  { label: "Bayern + Augsburg", value: "BY_AUGSBURG" },
  { label: "Berlin", value: "BE" },
  { label: "Brandenburg", value: "BB" },
  { label: "Bremen", value: "HB" },
  { label: "Hamburg", value: "HH" },
  { label: "Hessen", value: "HE" },
  { label: "Mecklenburg-Vorpommern", value: "MV" },
  { label: "Niedersachsen", value: "NI" },
  { label: "Nordrhein-Westfalen", value: "NW" },
  { label: "Rheinland-Pfalz", value: "RP" },
  { label: "Saarland", value: "SL" },
  { label: "Sachsen", value: "SN" },
  { label: "Sachsen-Anhalt", value: "ST" },
  { label: "Schleswig-Holstein", value: "SH" },
  { label: "Thüringen", value: "TH" },
];

const WEEKDAY_ORDER = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const RICHTUNG_ORDER = ["HIN", "RUECK"];

const LS_TOURS_KEY = "routing.cachedTours";
const LS_REPORT_CTX_KEY = "routing.cachedReportContext";
const LS_RUN_STATE_KEY = "routing.runState";
const TIME_LIMIT_TEXT = "Das gemeinsame Zeitbudget ist ausgeschöpft. Es wurde kein vollständig gültiger Tagesfahrplan gefunden. Das bisherige Ergebnis und dessen Downloads bleiben erhalten.";
const INFEASIBLE_TEXT = "Mit den aktuellen Vorgaben wurde kein zulässiger Fahrplan gefunden. "
  + "Das letzte gültige Ergebnis und dessen Downloads bleiben verfügbar.";

function formatDuration(totalSeconds) {
  const safe = Number.isFinite(totalSeconds)
    ? Math.max(0, Math.floor(totalSeconds))
    : 0;

  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;

  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  return hours > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
}

const RoutesPage = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [polling, setPolling] = useState(false);
  const pollIntervalId = useRef(null);
  const [solverStatus, setSolverStatus] = useState("NOT_SOLVING");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [bundesland, setBundesland] = useState(undefined);
  const [timeWindowMode, setTimeWindowMode] = useState("MARK_ONLY");
  const [calculationBasis, setCalculationBasis] = useState("DEPOT_TO_DEPOT");

  const [routesReady, setRoutesReady] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (_) {
      return null;
    }
  });
  const [reportContext, setReportContext] = useState(null);
  const [lastRuntimeSeconds, setLastRuntimeSeconds] = useState(null);
  const [precheck, setPrecheck] = useState(null);
  const [precheckLoading, setPrecheckLoading] = useState(true);
  const [tenderReport, setTenderReport] = useState(null);
  const [tenderReportLoading, setTenderReportLoading] = useState(false);

  const [errorText, setErrorText] = useState("");
  const [solvingSeconds, setSolvingSeconds] = useState(0);
  const [dots, setDots] = useState("");
  const solvingStartRef = useRef(null);

  const cancelRef = useRef(false);
  const hadSuccessBeforeRunRef = useRef(false);
  const [lastRunCancelled, setLastRunCancelled] = useState(false);

  const token = localStorage.getItem("token");

  const axiosAuth = axios.create({
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  const labels = getPlanningModeLabels(user?.planningMode);
  const tenderMode = user?.planningMode === "TENDER";

  const persistTours = (tours) => {
    try {
      localStorage.setItem(LS_TOURS_KEY, JSON.stringify(tours || []));
    } catch (_) {}
  };

  const persistReportContext = (ctx) => {
    try {
      if (!ctx) localStorage.removeItem(LS_REPORT_CTX_KEY);
      else localStorage.setItem(LS_REPORT_CTX_KEY, JSON.stringify(ctx));
    } catch (_) {}
  };

  const persistRunState = (state) => {
    try {
      if (!state) localStorage.removeItem(LS_RUN_STATE_KEY);
      else localStorage.setItem(LS_RUN_STATE_KEY, JSON.stringify(state));
    } catch (_) {}
  };

  const readRunState = () => {
    try {
      const raw = localStorage.getItem(LS_RUN_STATE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;

      return null;
    } catch {
      return null;
    }
  };

  const clearRunState = () => persistRunState(null);

  const restoreCache = () => {
    try {
      const rawTours = localStorage.getItem(LS_TOURS_KEY);
      if (rawTours) {
        const parsed = JSON.parse(rawTours);
        if (Array.isArray(parsed)) {
          setRoutes(parsed);
          if (parsed.length > 0) setRoutesReady(true);
        }
      }
    } catch (_) {}

    try {
      const rawCtx = localStorage.getItem(LS_REPORT_CTX_KEY);
      if (rawCtx) {
        const parsed = JSON.parse(rawCtx);
        if (parsed && typeof parsed === "object") {
          setReportContext(parsed);

          if (typeof parsed.runtimeSeconds === "number") {
            setLastRuntimeSeconds(parsed.runtimeSeconds);
          }

          setSolverStatus("NOT_SOLVING");
        }
      }
    } catch (_) {}
  };

  async function extractBackendMessage(err) {
    try {
      const res = err?.response;
      if (!res) {
        return err?.message || "Unbekannter Fehler (keine Server-Antwort)";
      }

      const data = res.data;

      if (data instanceof Blob) {
        const text = await data.text();

        try {
          const parsed = JSON.parse(text);
          return parsed.detail || parsed.message || parsed.error || text;
        } catch {
          return text || "Unbekannter Fehler (Blob)";
        }
      }

      if (data && typeof data === "object") {
        return data.detail || data.message || data.error || JSON.stringify(data);
      }

      if (typeof data === "string") return data;

      return err?.message || "Unbekannter Fehler";
    } catch (x) {
      console.error("extractBackendMessage failed:", x);
      return err?.message || "Unbekannter Fehler";
    }
  }

  function showBlockingModal(title, content) {
    try {
      Modal.warning({
        title,
        content: (
          <div>
            <p style={{ whiteSpace: "pre-wrap" }}>{content}</p>
            <ul style={{ marginLeft: 16 }}>
              <li>Reduziere ggf. die Anzahl an Passagieren/{labels.vehiclePlural}.</li>
              <li>Oder upgrade deinen Tarif in den Kontoeinstellungen.</li>
            </ul>
          </div>
        ),
        okText: "OK",
      });
    } catch {
      alert(`${title}\n\n${content}`);
    }
  }

  const fetchPrecheck = async ({ quiet = false } = {}) => {
    setPrecheckLoading(true);
    try {
      const res = await axiosAuth.get(`${ROUTE}/optimization-precheck`);
      setPrecheck(res.data);
      return res.data;
    } catch (e) {
      console.error("Precheck failed:", e);
      setPrecheck(null);
      if (!quiet) message.error("Vorprüfung konnte nicht geladen werden.");
      return null;
    } finally {
      setPrecheckLoading(false);
    }
  };

  const fetchTours = async () => {
    setLoading(true);

    try {
      const res = await axiosAuth.get(`${ROUTE}/tours`);

      const sorted = [...res.data].sort((a, b) => {
        if (a.busId !== b.busId) return a.busId - b.busId;

        const idxA = WEEKDAY_ORDER.indexOf(a.tag);
        const idxB = WEEKDAY_ORDER.indexOf(b.tag);

        if (idxA !== idxB) return idxA - idxB;

        return (
          RICHTUNG_ORDER.indexOf(a.richtung) -
          RICHTUNG_ORDER.indexOf(b.richtung)
        );
      });

      setRoutes(sorted);
      persistTours(sorted);
    } catch (e) {
      console.error(e);
      message.error("Touren konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenderReport = async (ctx) => {
    if (!ctx) return null;

    setTenderReportLoading(true);
    try {
      const res = await axiosAuth.get(`${ROUTE}/tender-report`, {
        params: {
          start: ctx.start,
          end: ctx.end,
          bundesland: ctx.bundesland,
        },
      });
      setTenderReport(res.data);
      return res.data;
    } catch (e) {
      console.error("Tender report failed:", e);
      setTenderReport(null);
      return null;
    } finally {
      setTenderReportLoading(false);
    }
  };

  const fetchSolverStatus = async () => {
    try {
      const res = await axiosAuth.get(`${ROUTE}/status`);
      setSolverStatus(res.data);

      if (res.data === "CANCEL_REQUESTED") {
        cancelRef.current = true;
      }

      return res.data;
    } catch (e) {
      console.error(e);
      setSolverStatus("NOT_SOLVING");
      return "NOT_SOLVING";
    }
  };

  const fetchUser = async () => {
    try {
      const res = await axiosAuth.get(`${ROUTE}/users/me`);
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
      window.dispatchEvent(new Event("planning-mode-updated"));
    } catch (_) {}
  };

  useEffect(() => {
    if (!polling) return;

    setSolvingSeconds(0);
    setDots("");

    if (!solvingStartRef.current) {
      solvingStartRef.current = Date.now();
    }

    const interval = setInterval(() => {
      const start = solvingStartRef.current ?? Date.now();
      const elapsedSec = Math.floor((Date.now() - start) / 1000);

      setSolvingSeconds(elapsedSec);
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 800);

    return () => clearInterval(interval);
  }, [polling]);

  const computeRuntimeSeconds = () => {
    const start = solvingStartRef.current;
    if (!start) return 0;

    return Math.max(0, Math.floor((Date.now() - start) / 1000));
  };

  const hasFilters = !!(startDate && endDate && bundesland);
  const hasLastSuccessful = !!(reportContext && routesReady);

  const startPollingLoop = () => {
    if (pollIntervalId.current) {
      clearInterval(pollIntervalId.current);
      pollIntervalId.current = null;
    }

    pollIntervalId.current = setInterval(async () => {
      const status = await fetchSolverStatus();

      if (status === "INFEASIBLE" || status === "SAVE_FAILED" || status === "TIME_LIMIT_REACHED") {
        solvingStartRef.current = null;
        clearInterval(pollIntervalId.current);
        pollIntervalId.current = null;
        setPolling(false);
        setLoading(false);
        setLastRunCancelled(false);
        clearRunState();

        setErrorText(status === "TIME_LIMIT_REACHED" ? TIME_LIMIT_TEXT : status === "SAVE_FAILED" ? "Das neue Ergebnis konnte nicht gespeichert werden. Das bisherige Ergebnis bleibt erhalten. Bitte erneut berechnen." : INFEASIBLE_TEXT);
        message.error(status === "TIME_LIMIT_REACHED" ? TIME_LIMIT_TEXT : status === "SAVE_FAILED" ? "Ergebnis konnte nicht gespeichert werden. Bitte erneut berechnen." : INFEASIBLE_TEXT);
        return;
      }

      if (status === "NOT_SOLVING") {
        const runtimeSec = computeRuntimeSeconds();
        solvingStartRef.current = null;

        clearInterval(pollIntervalId.current);
        pollIntervalId.current = null;

        setPolling(false);
        setLoading(false);

        if (cancelRef.current) {
          setLastRunCancelled(true);
          clearRunState();
          message.info("Routenberechnung wurde abgebrochen.");
          return;
        }

        await fetchTours();
        setRoutesReady(true);
        setLastRuntimeSeconds(runtimeSec);

        const rs = readRunState();

        const start =
          rs?.start || (startDate ? startDate.format("YYYY-MM-DD") : undefined);
        const end =
          rs?.end || (endDate ? endDate.format("YYYY-MM-DD") : undefined);
        const bl = rs?.bundesland || bundesland;

        const ctx = {
          start,
          end,
          bundesland: bl,
          timeWindowMode: rs?.timeWindowMode || timeWindowMode,
          calculationBasis: rs?.calculationBasis || calculationBasis,
          computedAt: new Date().toISOString(),
          runtimeSeconds: runtimeSec,
        };

        setReportContext(ctx);
        persistReportContext(ctx);
        clearRunState();
        await fetchTenderReport(ctx);

        setLastRunCancelled(false);
        message.success("Optimierung abgeschlossen! Touren aktualisiert.");
      }
    }, 3000);
  };

  const optimize = async () => {
    setErrorText("");

    if (!hasFilters) {
      message.warning("Bitte Zeitraum + Bundesland wählen.");
      return;
    }

    const currentPrecheck = await fetchPrecheck({ quiet: true });
    if (!currentPrecheck) {
      message.error("Optimierung nicht gestartet: Vorprüfung nicht verfügbar.");
      return;
    }
    if (!currentPrecheck.ready) {
      message.warning("Bitte behebe zuerst die roten Punkte der Vorprüfung.");
      return;
    }

    hadSuccessBeforeRunRef.current = hasLastSuccessful;

    cancelRef.current = false;
    setLastRunCancelled(false);

    setLoading(true);
    setPolling(true);
    setSolverStatus("SOLVING");
    solvingStartRef.current = Date.now();

    persistRunState({
      startedAt: solvingStartRef.current,
      start: startDate.format("YYYY-MM-DD"),
      end: endDate.format("YYYY-MM-DD"),
      bundesland,
      timeWindowMode,
      calculationBasis,
      cancelRequested: false,
    });

    try {
      await axiosAuth.get(`${ROUTE}/optimize`, {
        params: {
          start: startDate.format("YYYY-MM-DD"),
          end: endDate.format("YYYY-MM-DD"),
          bundesland,
          timeWindowMode,
          calculationBasis,
        },
      });
      message.success(
        "Optimierung gestartet. Du kannst die Seite wechseln – Status wird beim Zurückkehren fortgesetzt."
      );

      startPollingLoop();
    } catch (e) {
      console.error("Optimize failed:", e);

      const status = e?.response?.status ?? e?.request?.status ?? null;

      if (status === 403) message.warning("Tarifgrenze erreicht – Details folgen …");
      else if (status === 401) message.error("Nicht autorisiert. Bitte erneut einloggen.");
      else if (status === 409) message.warning("Es läuft bereits eine Optimierung. Bitte abbrechen oder warten.");
      else if (status === 422) message.warning("Vorprüfung nicht bestanden – bitte rote Punkte beheben.");
      else message.error("Optimierung fehlgeschlagen – Details folgen …");

      const backendMsg = await extractBackendMessage(e);

      const title =
        status === 403
          ? "Tarifgrenze erreicht"
          : status === 401
          ? "Nicht autorisiert"
          : status === 422
          ? "Vorprüfung nicht bestanden"
          : "Optimierung fehlgeschlagen";

      const content =
        backendMsg ||
        (status === 403
          ? "Tariflimit überschritten oder Abo abgelaufen."
          : "Unbekannter Fehler.");

      setErrorText(`${title}: ${content}`);
      showBlockingModal(title, content);

      setPolling(false);
      setLoading(false);
      setSolverStatus("NOT_SOLVING");
      cancelRef.current = false;
      setLastRunCancelled(false);
      solvingStartRef.current = null;

      clearRunState();

      if (pollIntervalId.current) {
        clearInterval(pollIntervalId.current);
      }

      pollIntervalId.current = null;
    } finally {
      setLoading(false);
    }
  };

  const cancelOptimization = async () => {
    try {
      cancelRef.current = true;

      const rs = readRunState();
      persistRunState({
        ...(rs || {}),
        cancelRequested: true,
      });

      await axiosAuth.post(`${ROUTE}/cancel`);
      message.info("Abbruch angefordert – Solver stoppt so schnell wie möglich …");
    } catch (e) {
      console.error(e);
      cancelRef.current = false;
      message.error("Abbruch fehlgeschlagen.");
    }
  };

  const getDownloadContext = () => {
    if (reportContext?.start && reportContext?.end && reportContext?.bundesland) {
      return reportContext;
    }

    if (hasFilters) {
      return {
        start: startDate.format("YYYY-MM-DD"),
        end: endDate.format("YYYY-MM-DD"),
        bundesland,
      };
    }

    return null;
  };

  const handleDownloadCostsPdf = async () => {
    const ctx = getDownloadContext();
    if (!ctx) return;

    const url = `${ROUTE}/pdf-report?start=${ctx.start}&end=${ctx.end}&bundesland=${ctx.bundesland}`;

    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "fahrplan-uebersicht.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      message.error("PDF-Download fehlgeschlagen.");
    }
  };

  const handleDownloadDriverZip = async () => {
    const ctx = getDownloadContext();
    if (!ctx) return;

    const url = `${ROUTE}/pdf-driver-all?start=${ctx.start}&end=${ctx.end}&bundesland=${ctx.bundesland}`;

    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const cd = response.headers["content-disposition"] || "";
      const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
      const filename = decodeURIComponent(
        (match && (match[1] || match[2])) || "fahrplaene.zip"
      );

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/zip" })
      );

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      message.success("Fahrpläne und Taxiübersicht (ZIP) werden heruntergeladen.");
    } catch (err) {
      console.error(err);
      message.error("ZIP-Download fehlgeschlagen.");
    }
  };

  const handleDownloadTenderReport = async () => {
    const ctx = getDownloadContext();
    if (!ctx) return;

    const url = `${ROUTE}/tender-report/export?start=${ctx.start}&end=${ctx.end}&bundesland=${ctx.bundesland}`;

    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], {
          type: "application/pdf",
        })
      );

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "ausschreibungsbedarf.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      message.success("Ausschreibungsbedarf wird heruntergeladen.");
    } catch (err) {
      console.error(err);
      message.error("PDF-Download fehlgeschlagen.");
    }
  };

  const onChangeStartDate = (v) => setStartDate(v);
  const onChangeEndDate = (v) => setEndDate(v);
  const onChangeBundesland = (v) => setBundesland(v);

  const applyLastReportContextToFilters = () => {
    if (!reportContext) return;

    setStartDate(dayjs(reportContext.start));
    setEndDate(dayjs(reportContext.end));
    setBundesland(reportContext.bundesland);
    setTimeWindowMode(reportContext.timeWindowMode || "MARK_ONLY");
    setCalculationBasis(reportContext.calculationBasis || "DEPOT_TO_DEPOT");

    message.info("Filter auf letzte Berechnung gesetzt.");
  };

  const formatNumber = (value, digits = 2) => {
    const numeric = Number(value);
    return Number.isFinite(numeric)
      ? numeric.toLocaleString("de-DE", {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        })
      : "-";
  };

  const formatEuro = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric)
      ? numeric.toLocaleString("de-DE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "-";
  };

  const formatInteger = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric)
      ? numeric.toLocaleString("de-DE", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : "-";
  };

  const renderResultStatistic = (title, value, formatter) => (
    <div className="tender-stat">
      <Statistic title={title} value={value ?? 0} formatter={formatter} />
    </div>
  );

  const formatResultNumber = (value, digits = 1) => formatNumber(value, digits);

  const formatResultCost = (value) => formatEuro(value);

  const statisticSummary = tenderReport?.summary;
  const timeWindowViolationCount = routes.filter(
    (route) => route?.timeWindowFeasible === false
  ).length;

  useEffect(() => {
    return () => {
      if (pollIntervalId.current) {
        clearInterval(pollIntervalId.current);
      }
    };
  }, []);

  useEffect(() => {
    restoreCache();
    fetchUser();
    fetchPrecheck({ quiet: true });

    (async () => {
      const rs = readRunState();
      const status = await fetchSolverStatus();

      if (status === "INFEASIBLE" || status === "SAVE_FAILED" || status === "TIME_LIMIT_REACHED") {
        clearRunState();
        setErrorText(status === "TIME_LIMIT_REACHED" ? TIME_LIMIT_TEXT : status === "SAVE_FAILED" ? "Das neue Ergebnis konnte nicht gespeichert werden. Das bisherige Ergebnis bleibt erhalten. Bitte erneut berechnen." : INFEASIBLE_TEXT);
        return;
      }

      const isBackendRunning =
        status === "SOLVING" || status === "CANCEL_REQUESTED";
      const isRunStateRunning = !!rs && typeof rs.startedAt === "number";

      if (!isBackendRunning && !isRunStateRunning) {
        try {
          const saved = await axiosAuth.get(`${ROUTE}/saved-result`);
          if (saved.status === 200 && saved.data?.context) {
            const ctx = saved.data.context;
            setReportContext(ctx);
            persistReportContext(ctx);
            setRoutes(saved.data.tours || []);
            persistTours(saved.data.tours || []);
            setRoutesReady(true);
            setStartDate(dayjs(ctx.start));
            setEndDate(dayjs(ctx.end));
            setBundesland(ctx.bundesland);
            setCalculationBasis(ctx.calculationBasis || "DEPOT_TO_DEPOT");
            setTimeWindowMode(ctx.timeWindowMode || "MARK_ONLY");
          }
        } catch {
          // Cached display remains available; the results page offers an explicit retry.
        }
      }

      if (isBackendRunning || isRunStateRunning) {
        setPolling(true);
        setLoading(true);
        setLastRunCancelled(false);

        cancelRef.current =
          status === "CANCEL_REQUESTED" || rs?.cancelRequested === true;

        solvingStartRef.current = rs?.startedAt || Date.now();

        if (rs?.start && rs?.end && rs?.bundesland) {
          if (!startDate) setStartDate(dayjs(rs.start));
          if (!endDate) setEndDate(dayjs(rs.end));
          if (!bundesland) setBundesland(rs.bundesland);
          if (rs?.timeWindowMode) setTimeWindowMode(rs.timeWindowMode);
          if (rs?.calculationBasis) setCalculationBasis(rs.calculationBasis);
        }

        startPollingLoop();
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportContext?.start && reportContext?.end && reportContext?.bundesland) {
      fetchTenderReport(reportContext);
    } else {
      setTenderReport(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportContext?.start, reportContext?.end, reportContext?.bundesland]);

  const canDownload = hasLastSuccessful;
  const canOptimize =
    hasFilters && precheck?.ready === true && !precheckLoading && !polling && !loading;

  const solvingUiText = useMemo(() => {
    if (!polling) return "";

    if (solverStatus === "CANCEL_REQUESTED" || cancelRef.current) {
      return `Abbruch wird durchgeführt${dots}`;
    }

    return `Routen werden berechnet${dots}`;
  }, [polling, dots, solverStatus]);

  const showStatusBox =
    polling || lastRunCancelled || (hasLastSuccessful && lastRuntimeSeconds !== null);

  const statusBoxStyle = polling
    ? {
        border: `1px solid ${COLORS.border}`,
        borderLeft: `6px solid ${COLORS.primary}`,
        background: COLORS.primaryLight,
      }
    : lastRunCancelled
    ? {
        border: "1px solid #ffccc7",
        borderLeft: "6px solid #ff4d4f",
        background: "#fff2f0",
      }
    : {
        border: `1px solid ${COLORS.border}`,
        borderLeft: `6px solid ${COLORS.primary}`,
        background: "#F4FBFC",
      };

  const statusTitle = polling
    ? solvingUiText
    : lastRunCancelled
    ? "Routenberechnung wurde abgebrochen"
    : "Routen wurden berechnet";

  const statusLine = polling
    ? `Läuft seit ${formatDuration(solvingSeconds)} · Status: ${
        solverStatus || "SOLVING"
      }`
    : lastRunCancelled
    ? hadSuccessBeforeRunRef.current
      ? "Es wurden keine neuen Routen übernommen. Vorherige Berechnung bleibt erhalten."
      : "Es wurden keine Routen übernommen."
    : `Laufzeit ${formatDuration(lastRuntimeSeconds ?? 0)} · Status: FINISH`;

  const uiCtx = useMemo(() => {
    if (!hasFilters) return null;

    return {
      start: startDate.format("YYYY-MM-DD"),
      end: endDate.format("YYYY-MM-DD"),
      bundesland,
      timeWindowMode,
      calculationBasis,
    };
  }, [hasFilters, startDate, endDate, bundesland, timeWindowMode, calculationBasis]);

  const filtersDifferFromLast = useMemo(() => {
    if (!reportContext || !uiCtx) return false;

    return (
      reportContext.start !== uiCtx.start ||
      reportContext.end !== uiCtx.end ||
      reportContext.bundesland !== uiCtx.bundesland ||
      (reportContext.timeWindowMode || "MARK_ONLY") !== uiCtx.timeWindowMode ||
      (reportContext.calculationBasis || "DEPOT_TO_DEPOT") !== uiCtx.calculationBasis
    );
  }, [reportContext, uiCtx]);

  dayjs.locale("de");

  return (
    <ConfigProvider locale={deDE}>
      <div className="routes-page">
        <h2 className="app-page-title">Routenübersicht</h2>

        {timeWindowViolationCount > 0
          && (reportContext?.timeWindowMode || "MARK_ONLY") === "MARK_ONLY" && (
          <Alert
            showIcon
            type="warning"
            style={{ marginBottom: 14 }}
            message={`${timeWindowViolationCount} Touren können die Zeitfenster nicht vollständig einhalten`}
            description="Die Optimierung wurde trotzdem übernommen. Die betroffenen Touren und Abweichungen sind im Fahrplan gekennzeichnet."
          />
        )}

        {errorText && (
          <div
            role="alert"
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              border: "1px solid #ff4d4f",
              background: "#fff1f0",
              color: "#a8071a",
              borderRadius: 10,
            }}
          >
            {errorText}
          </div>
        )}

        {hasLastSuccessful && reportContext && (
          <div style={{ marginBottom: 14 }}>
            <Alert
              showIcon
              message="Letzte berechnete Daten für Downloads"
              description={
                <div>
                  <div style={{ marginBottom: 6 }}>
                    Zeitraum:{" "}
                    <b>{dayjs(reportContext.start).format("DD.MM.YYYY")}</b>{" "}
                    bis <b>{dayjs(reportContext.end).format("DD.MM.YYYY")}</b>{" "}
                    · Bundesland: <b>{reportContext.bundesland}</b>
                    {" · "}Berechnungsgrundlage:{" "}
                    <b>
                      {(reportContext.calculationBasis || "DEPOT_TO_DEPOT") === "OCCUPIED_ONLY"
                        ? "nur besetzte Fahrt"
                        : "Depot bis Depot"}
                    </b>
                    {lastRunCancelled && (
                      <span style={{ marginLeft: 10 }}>
                        <Tag color="error">Letzter Lauf abgebrochen</Tag>
                      </span>
                    )}
                  </div>

                  {filtersDifferFromLast && (
                    <div style={{ marginBottom: 8 }}>
                      Hinweis: Deine aktuellen Filter weichen davon ab. Downloads
                      beziehen sich weiterhin auf die letzte Berechnung, bis du neu
                      berechnest.
                    </div>
                  )}

                  {filtersDifferFromLast && (
                    <Button size="small" onClick={applyLastReportContextToFilters}>
                      Filter auf letzte Berechnung setzen
                    </Button>
                  )}
                </div>
              }
              type={filtersDifferFromLast ? "warning" : "info"}
              style={{ borderRadius: 12 }}
            />
          </div>
        )}

        <Form
          layout="inline"
          style={{
            marginBottom: 16,
            padding: 16,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 14,
            background: "#FFFFFF",
          }}
        >
          <Form.Item label="Zeitraum">
            <DatePicker
              value={startDate}
              onChange={onChangeStartDate}
              placeholder="Start"
              format="DD.MM.YYYY"
              style={{ width: 130, marginRight: 8 }}
              locale="de"
            />

            <span style={{ margin: "0 8px", color: COLORS.muted }}>bis</span>

            <DatePicker
              value={endDate}
              onChange={onChangeEndDate}
              placeholder="Ende"
              format="DD.MM.YYYY"
              style={{ width: 130 }}
              locale="de"
            />
          </Form.Item>

          <Form.Item label="Bundesland">
            <Select
              allowClear
              placeholder="Bitte auswählen"
              value={bundesland}
              onChange={onChangeBundesland}
              style={{ width: 300 }}
              options={BUNDESLAENDER}
            />
          </Form.Item>

          <Form.Item
            label="Zeitfenster"
            style={{ width: "100%", marginTop: 8 }}
          >
            <Radio.Group
              value={timeWindowMode}
              onChange={(event) => setTimeWindowMode(event.target.value)}
              disabled={polling || loading}
            >
              <Space direction="vertical" size={4}>
                <Radio value="MARK_ONLY">
                  Verletzungen nur markieren
                </Radio>
                <Radio value="ENFORCE">
                  Zeitfenster müssen eingehalten werden
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Fahrzeit, Kilometer und Kosten"
            style={{ width: "100%", marginTop: 8 }}
          >
            <Radio.Group
              value={calculationBasis}
              onChange={(event) => setCalculationBasis(event.target.value)}
              disabled={polling || loading}
            >
              <Space direction="vertical" size={4}>
                <Radio value="DEPOT_TO_DEPOT">
                  Gesamte Fahrt vom Startdepot bis zum Enddepot
                </Radio>
                <Radio value="OCCUPIED_ONLY">
                  Nur besetzte Fahrt von der ersten Abholung bis zum letzten Ausstieg
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item>
            <Space wrap>
              <Button
                type={canDownload ? "primary" : "default"}
                onClick={handleDownloadCostsPdf}
                disabled={!canDownload}
              >
                Kostenübersicht herunterladen
              </Button>

              <Button
                type={canDownload ? "primary" : "default"}
                onClick={handleDownloadDriverZip}
                disabled={!canDownload}
              >
                Fahrpläne & Taxiübersicht herunterladen
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <div
          style={{
            marginBottom: 16,
            padding: 14,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 14,
            background: "#FFFFFF",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontWeight: 700, color: COLORS.text }}>
                Prüfen vor Optimieren
              </div>
              <div style={{ fontSize: 12, color: COLORS.muted }}>
                Rote Punkte müssen vor dem Start behoben werden.
              </div>
            </div>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={precheckLoading}
              onClick={() => fetchPrecheck()}
              disabled={polling}
            >
              Neu prüfen
            </Button>
          </div>

          {precheckLoading && !precheck ? (
            <Spin size="small" />
          ) : !precheck ? (
            <Alert
              type="error"
              showIcon
              message="Vorprüfung nicht verfügbar"
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 10,
              }}
            >
                  {precheck.checks.map((check) => {
                    const isGreen = check.status === "GREEN";
                    const isYellow = check.status === "YELLOW";
                    const title =
                      check.key === "capacity" ? labels.vehicleCapacity : check.label;
                const color = isGreen ? "#52c41a" : isYellow ? "#faad14" : "#ff4d4f";
                const background = isGreen ? "#f6ffed" : isYellow ? "#fffbe6" : "#fff2f0";
                return (
                  <div
                    key={check.key}
                    style={{
                      padding: "10px 12px",
                      border: `1px solid ${color}55`,
                      borderRadius: 10,
                      background,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        aria-label={isGreen ? "grün" : isYellow ? "gelb" : "rot"}
                        style={{
                          width: 12,
                          height: 12,
                          flex: "0 0 12px",
                          borderRadius: "50%",
                          background: color,
                        }}
                      />
                      <b>{title}</b>
                    </div>
                    <div style={{ marginTop: 5, fontSize: 13, color: COLORS.text }}>
                      {check.summary}
                    </div>
                    {check.details?.length > 0 && (
                      <ul
                        style={{
                          margin: "6px 0 0 18px",
                          padding: 0,
                          fontSize: 12,
                          color: isYellow ? "#874d00" : "#a8071a",
                        }}
                      >
                        {(check.key === "capacity" ? check.details : check.details.slice(0, 3)).map((detail) => (
                          <li key={detail}>{detail}</li>
                        ))}
                        {check.key !== "capacity" && check.details.length > 3 && (
                          <li>… und {check.details.length - 3} weitere</li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {polling && (
          <div style={{ marginBottom: 14 }}>
            <Alert
              showIcon
              icon={<InfoCircleOutlined style={{ color: COLORS.primary }} />}
              message={
                <span style={{ color: COLORS.primaryDark }}>
                  Hinweis zur Berechnungsdauer
                </span>
              }
              description="Die Berechnung läuft auf dem Server weiter. Du kannst die Seite wechseln – wenn du zurückkommst, wird der Status weiter angezeigt."
              style={{
                background: COLORS.primaryLight,
                border: `1px solid ${COLORS.border}`,
                borderLeft: `6px solid ${COLORS.primary}`,
                borderRadius: 12,
              }}
            />
          </div>
        )}

        <Space style={{ marginBottom: 16, alignItems: "center" }} wrap>
          <Button
            onClick={optimize}
            type={canOptimize ? "primary" : "default"}
            loading={polling || loading}
            disabled={!canOptimize}
          >
            Routen berechnen
          </Button>

          {polling && (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={cancelOptimization}
              disabled={solverStatus === "CANCEL_REQUESTED"}
            >
              {solverStatus === "CANCEL_REQUESTED"
                ? "Abbruch läuft…"
                : "Abbrechen"}
            </Button>
          )}

          {showStatusBox && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 12px",
                borderRadius: 12,
                ...statusBoxStyle,
              }}
            >
              {polling ? (
                <Spin indicator={<LoadingOutlined spin />} />
              ) : (
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: lastRunCancelled ? "#ff4d4f" : COLORS.primary,
                  }}
                  aria-hidden="true"
                />
              )}

              <div style={{ minWidth: 320 }}>
                <div
                  style={{
                    fontWeight: 700,
                    color: polling
                      ? COLORS.primaryDark
                      : lastRunCancelled
                      ? "#a8071a"
                      : COLORS.text,
                  }}
                >
                  {statusTitle}
                </div>

                <div style={{ fontSize: 12, color: COLORS.muted }}>
                  {statusLine}
                </div>
              </div>
            </div>
          )}

          <Link to="/app/results"><Button>Gespeicherte Ergebnisse ansehen</Button></Link>
          {!polling && lastRunCancelled && <Tag color="error">Abgebrochen</Tag>}

          {!polling && !lastRunCancelled && solverStatus === "NOT_SOLVING" && (
            <Tag color="success">Bereit</Tag>
          )}

          {!polling && !hasFilters && <Tag>Bitte Zeitraum + Bundesland wählen</Tag>}
          {!polling && hasFilters && precheck && !precheck.ready && (
            <Tag color="error">Vorprüfung nicht bestanden</Tag>
          )}
        </Space>

        {hasLastSuccessful && (
          <div style={{ marginBottom: 18 }}>
            <Card
              title={tenderMode ? "Ausschreibungsbedarf" : "Ergebnisübersicht Eigenfuhrpark"}
              extra={
                tenderMode && tenderReport ? (
                  <Button type="primary" size="small" onClick={handleDownloadTenderReport}>
                    PDF herunterladen
                  </Button>
                ) : null
              }
              loading={tenderReportLoading && !tenderReport}
              style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 14,
                background: "#FFFFFF",
              }}
            >
              {!statisticSummary ? (
                <Alert
                  type="info"
                  showIcon
                  message="Noch keine Ergebnisdaten verfügbar"
                  description="Berechne zuerst Routen. Danach werden die Kennzahlen aus der letzten erfolgreichen Optimierung abgeleitet."
                />
              ) : (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {renderResultStatistic(
                      tenderMode ? "Benötigte Busse" : "Eingesetzte Busse",
                      statisticSummary.requiredProfiles,
                      formatInteger
                    )}
                    {renderResultStatistic(
                      "Fahrgäste",
                      statisticSummary.assignedPassengers,
                      formatInteger
                    )}
                    {renderResultStatistic(
                      "km im Zeitraum",
                      statisticSummary.periodKm,
                      (value) => formatResultNumber(value, 1)
                    )}
                    {renderResultStatistic(
                      "Stunden im Zeitraum",
                      statisticSummary.periodHours,
                      (value) => formatResultNumber(value, 1)
                    )}
                    {renderResultStatistic(
                      "Kosten im Zeitraum",
                      statisticSummary.periodCost,
                      formatResultCost
                    )}
                  </div>

                  {tenderMode && (
                    <Alert
                      type="info"
                      showIcon
                      message="Details stehen im Ausschreibungsbedarf-PDF"
                      description="Die Detailtabellen werden hier bewusst ausgeblendet, damit die Ergebnisübersicht auf die wichtigsten Kennzahlen fokussiert bleibt."
                    />
                  )}
                </Space>
              )}
            </Card>
          </div>
        )}

        <style>{`
          .routes-page h2 {
            margin-top: 0;
            margin-bottom: 18px;
            color: #1F2937;
            font-weight: 700;
          }

          .tender-stat {
            border: 1px solid #DCE7F3;
            border-radius: 8px;
            padding: 12px;
            background: #F8FCFD;
          }
        `}</style>
      </div>
    </ConfigProvider>
  );
};

export default RoutesPage;
